"use client";

import { useState } from "react";

export default function Home() {
  const [formData, setFormData] = useState({
    customer_name: "",
    phone_number: "+968",
    shoulder: "",
    chest: "",
    waist: "",
    length: "",
  });

  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // STOP the page from reloading/refreshing
    console.log("Submit button clicked!"); // DEBUG
    setStatus("loading");

    try {
      console.log("Sending data to API..."); // DEBUG
      const response = await fetch("https://api.alshakilee.com/api/measurements", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          customer_name: formData.customer_name,
          phone_number: formData.phone_number,
          shoulder: parseFloat(formData.shoulder),
          chest: parseFloat(formData.chest),
          waist: parseFloat(formData.waist),
          length: parseFloat(formData.length),
        }),
      });

      console.log("Response Status:", response.status); // DEBUG

      if (!response.ok) {
        const errTxt = await response.text();
        throw new Error(`API Error ${response.status}: ${errTxt}`);
      }

      const result = await response.json();
      console.log("API Result:", result); // DEBUG

      if (result.status === "success") {
            // 1. Save data to browser memory for the PDF
            localStorage.setItem("last_measurement", JSON.stringify(formData));
            
            // 2. Redirect to the success page
            window.location.href = "/measure/success";
        }
    } catch (error: any) {
      console.error("Submission Error:", error); // DEBUG
      setStatus("error");
      setErrorMessage(error.message);
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center p-6 font-sans">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
        
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Alshakilee Tailoring</h1>
          <p className="text-sm text-gray-500 mt-2">Submit your custom measurements below.</p>
        </div>

        {status === "success" ? (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-6 text-center">
            <svg className="w-12 h-12 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <h3 className="text-lg font-bold mb-1">Measurements Received!</h3>
            <p className="text-sm">We have securely saved your profile. You will receive a WhatsApp confirmation shortly.</p>
            <button onClick={() => setStatus("idle")} className="mt-4 text-sm font-semibold text-green-700 hover:text-green-900 underline">
              Submit Another
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input required type="text" name="customer_name" value={formData.customer_name} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-black focus:border-black outline-none transition-all text-gray-900" placeholder="Ahmed Al-Balushi" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number</label>
                <input required type="text" name="phone_number" value={formData.phone_number} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-black focus:border-black outline-none transition-all text-gray-900" />
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shoulder (in)</label>
                  <input required type="number" step="0.5" name="shoulder" value={formData.shoulder} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-black focus:border-black outline-none transition-all text-gray-900" placeholder="18.5" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chest (in)</label>
                  <input required type="number" step="0.5" name="chest" value={formData.chest} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-black focus:border-black outline-none transition-all text-gray-900" placeholder="42.0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Waist (in)</label>
                  <input required type="number" step="0.5" name="waist" value={formData.waist} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-black focus:border-black outline-none transition-all text-gray-900" placeholder="36.0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Length (in)</label>
                  <input required type="number" step="0.5" name="length" value={formData.length} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-black focus:border-black outline-none transition-all text-gray-900" placeholder="58.0" />
                </div>
              </div>
            </div>

            {status === "error" && (
              <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100">
                <strong>Error:</strong> {errorMessage}
              </div>
            )}

            <button 
              type="submit" 
              disabled={status === "loading"}
              className="w-full bg-black text-white font-semibold py-4 rounded-lg hover:bg-gray-800 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center shadow-lg shadow-gray-300"
            >
              {status === "loading" ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  Processing...
                </span>
              ) : (
                "Save Measurements"
              )}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}